import React, { useState } from 'react';
import {
  ArrowLeft,
  History,
  LoaderCircle,
  Sparkles,
} from 'lucide-react';

const sidebarThemeClasses = {
  brd: 'workspace-hero-pdf',
  requirements: 'workspace-hero-requirements',
  api: 'workspace-hero-api',
  excel: 'workspace-hero-excel',
  capture: 'workspace-hero-capture',
  requests: 'workspace-hero-requests',
};

const pageThemeClasses = {
  brd: 'workspace-product-brd',
  requirements: 'workspace-product-requirements',
  api: 'workspace-product-api',
  excel: 'workspace-product-excel',
  capture: 'workspace-product-capture',
  requests: 'workspace-product-requests',
};

const stagePillToneClasses = {
  danger: 'workspace-stage-pill-danger',
  success: 'workspace-stage-pill-success',
};

const joinClasses = (...classes) => classes.filter(Boolean).join(' ');

export const WorkspaceBackButton = ({
  label = 'Back',
  onClick,
  disabled = false,
  title,
  className = '',
  size = 'sm',
  ariaLabel,
}) => {
  const sizeClass = size === 'xs'
    ? 'h-8 px-2.5 py-0 text-xs'
    : 'h-9 px-3 py-0 text-sm';
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      title={title}
      aria-label={ariaLabel || title || label}
      className={joinClasses(
        'secondary-btn',
        sizeClass,
        'workspace-back-button',
        disabled && 'disabled:cursor-not-allowed disabled:opacity-50',
        className
      )}
    >
      <ArrowLeft className="h-4 w-4" aria-hidden="true" />
      {label}
    </button>
  );
};

export const WorkspacePage = ({
  theme = 'brd',
  containerClassName = 'max-w-[1680px] 2xl:max-w-[1720px]',
  sidebar,
  children,
}) => (
  <div
    className={joinClasses(
      'page-shell',
      'workspace-page-shell',
      'workspace-product-page',
      pageThemeClasses[theme] || pageThemeClasses.brd
    )}
  >
    <div className={`mx-auto flex w-full flex-col gap-6 ${containerClassName}`}>
      <section className="workspace-grid workspace-grid-sidebar-hero workspace-unified-grid">
        {sidebar}
        <main className="workspace-main-panel">{children}</main>
      </section>
    </div>
  </div>
);

export const WorkspaceRouteSkeleton = ({
  theme = 'brd',
  containerClassName = 'max-w-[1680px] 2xl:max-w-[1720px]',
  cardCount = 3,
  rowCount = 3,
}) => {
  const pageTheme = pageThemeClasses[theme] || pageThemeClasses.brd;
  const sidebarTheme = sidebarThemeClasses[theme] || sidebarThemeClasses.brd;

  return (
    <div className={`page-shell workspace-page-shell workspace-product-page ${pageTheme}`}>
      <div className={`mx-auto flex w-full flex-col gap-6 ${containerClassName}`}>
        <section className="workspace-grid workspace-grid-sidebar-hero workspace-unified-grid">
          <aside className={`workspace-sidebar-hero workspace-sidebar-standard ${sidebarTheme}`}>
            <div className="workspace-sidebar-grid" aria-hidden="true" />
            <div className="workspace-sidebar-orb" aria-hidden="true" />
            <div className="relative z-[1] animate-pulse">
              <div className="h-7 w-36 rounded-full bg-white/20" />
              <div className="mt-7 space-y-3">
                <div className="h-8 w-4/5 rounded bg-white/25" />
                <div className="h-8 w-2/3 rounded bg-white/25" />
                <div className="h-8 w-3/4 rounded bg-white/25" />
              </div>
              <div className="mt-7 space-y-2">
                <div className="h-4 w-full rounded bg-white/20" />
                <div className="h-4 w-4/5 rounded bg-white/20" />
              </div>
              <div className="mt-8 h-12 rounded-xl border border-white/25 bg-white/15" />
            </div>

            <div className="workspace-sidebar-metrics">
              {[0, 1, 2, 3].map((item) => (
                <div key={`workspace-route-sidebar-skeleton-${item}`} className="workspace-metric-card">
                  <div className="animate-pulse">
                    <div className="h-3 w-28 rounded bg-white/20" />
                    <div className="mt-3 h-4 w-3/5 rounded bg-white/30" />
                  </div>
                </div>
              ))}
            </div>
          </aside>

          <main className="workspace-main-panel">
            <div className="workspace-stage-card workspace-stage-card-standard-height">
              <div className="workspace-stage-header">
                <div className="animate-pulse">
                  <div className="h-3 w-32 rounded bg-slate-200" />
                  <div className="mt-3 h-7 w-48 rounded bg-slate-200" />
                  <div className="mt-3 h-4 w-72 max-w-full rounded bg-slate-100" />
                </div>
                <div className="h-7 w-40 animate-pulse rounded-full bg-cyan-50" />
              </div>

              <div className="workspace-stage-body workspace-stage-body-checklisted">
                <div className="workspace-stage-content space-y-4">
                  <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
                    <div className="animate-pulse space-y-4">
                      <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
                        <div className="space-y-3">
                          <div className="h-3 w-28 rounded bg-blue-100" />
                          <div className="h-7 w-64 max-w-full rounded bg-slate-200" />
                          <div className="h-4 w-80 max-w-full rounded bg-slate-100" />
                        </div>
                        <div className="flex flex-wrap gap-2">
                          <div className="h-10 w-28 rounded-xl bg-slate-100" />
                          <div className="h-10 w-32 rounded-xl bg-slate-100" />
                        </div>
                      </div>
                      <div className="grid gap-3 md:grid-cols-3">
                        {Array.from({ length: cardCount }).map((_, card) => (
                          <div
                            key={`workspace-route-card-skeleton-${card}`}
                            className="rounded-2xl border border-slate-200 p-4"
                          >
                            <div className="h-10 w-10 rounded-xl bg-slate-100" />
                            <div className="mt-4 h-4 w-3/5 rounded bg-slate-200" />
                            <div className="mt-3 h-3 w-full rounded bg-slate-100" />
                            <div className="mt-2 h-3 w-4/5 rounded bg-slate-100" />
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>

                  <section className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
                    <div className="flex items-center justify-between gap-3 border-b border-slate-100 px-4 py-3">
                      <div className="h-4 w-48 animate-pulse rounded bg-slate-200" />
                      <div className="h-7 w-24 animate-pulse rounded-full bg-slate-100" />
                    </div>
                    <div className="divide-y divide-slate-100">
                      {Array.from({ length: rowCount }).map((_, row) => (
                        <div key={`workspace-route-row-skeleton-${row}`} className="px-4 py-4">
                          <div className="animate-pulse space-y-3">
                            <div className="h-4 w-3/4 rounded bg-slate-200" />
                            <div className="h-3 w-full rounded bg-slate-100" />
                            <div className="h-3 w-2/3 rounded bg-slate-100" />
                          </div>
                        </div>
                      ))}
                    </div>
                  </section>
                </div>

                <div className="soft-card-muted workspace-stage-checklist workspace-checklist">
                  <div className="workspace-checklist-heading">
                    <div className="h-4 w-36 animate-pulse rounded bg-slate-200" />
                    <div className="h-6 w-24 animate-pulse rounded-full bg-slate-100" />
                  </div>
                  <div className="workspace-checklist-grid">
                    {[0, 1, 2].map((item) => (
                      <div key={`workspace-route-checklist-skeleton-${item}`} className="workspace-checklist-item">
                        <span className="workspace-checklist-index" aria-hidden="true">
                          {String(item + 1).padStart(2, '0')}
                        </span>
                        <div className="min-w-0 flex-1 animate-pulse">
                          <div className="h-4 w-3/5 rounded bg-slate-200" />
                          <div className="mt-2 h-3 w-full rounded bg-slate-100" />
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </main>
        </section>
      </div>
    </div>
  );
};

export const WorkspaceSidebar = ({
  theme = 'brd',
  kicker,
  title,
  description,
  historyLoading = false,
  historyDisabled = false,
  onHistory,
  metrics = [],
  action = null,
  footer = null,
}) => {
  const resolvedAction = action === false
    ? null
    : action || {
      label: 'History',
      loadingLabel: 'Loading history...',
      icon: History,
      loading: historyLoading,
      disabled: historyDisabled,
      onClick: onHistory,
    };
  const ActionIcon = resolvedAction?.icon || History;
  const actionLoading = Boolean(resolvedAction?.loading);
  const FooterIcon = footer?.icon || Sparkles;
  const footerTitle = footer?.title ?? 'One connected product flow';
  const footerDescription = footer?.description
    ?? 'Intake · Coverage · Automation · Delivery';

  return (
    <aside
      aria-label={`${kicker} summary`}
      className={joinClasses(
        'workspace-sidebar-hero',
        'workspace-sidebar-standard',
        sidebarThemeClasses[theme] || sidebarThemeClasses.brd
      )}
    >
      <div className="workspace-sidebar-grid" aria-hidden="true" />
      <div className="workspace-sidebar-orb" aria-hidden="true" />

      <div className="workspace-hero-main">
        <p className="workspace-kicker">{kicker}</p>
        <h1 className="workspace-sidebar-title">{title}</h1>
        <p className="workspace-sidebar-subtitle">{description}</p>

        {resolvedAction && (
        <div className="workspace-hero-actions">
          <button
            type="button"
            onClick={resolvedAction.onClick}
            disabled={resolvedAction.disabled || actionLoading}
            aria-busy={actionLoading}
            className="workspace-history-button"
          >
            {actionLoading
              ? <LoaderCircle className="h-4 w-4 animate-spin" aria-hidden="true" />
              : <ActionIcon className="h-4 w-4" aria-hidden="true" />}
            {actionLoading
              ? resolvedAction.loadingLabel || resolvedAction.label
              : resolvedAction.label}
          </button>
        </div>
        )}
      </div>

      <dl className="workspace-sidebar-metrics">
        {metrics.map((metric) => (
          <div className="workspace-metric-card" key={metric.label}>
            <dt className="workspace-metric-label">{metric.label}</dt>
            <dd
              className="workspace-metric-value"
              title={metric.title ?? String(metric.value ?? '')}
            >
              {metric.value}
            </dd>
          </div>
        ))}
      </dl>

      {footer !== false && (
        <div className="workspace-sidebar-footer">
          <span className="workspace-sidebar-footer-icon" aria-hidden="true">
            <FooterIcon className="h-4 w-4" />
          </span>
          <div>
            <p>{footerTitle}</p>
            <span>{footerDescription}</span>
          </div>
        </div>
      )}
    </aside>
  );
};

export const WorkspaceStageHeader = ({
  kicker,
  title,
  description,
  actions = null,
  status = null,
}) => {
  const StatusIcon = status?.icon;
  const [statusDetailsOpen, setStatusDetailsOpen] = useState(false);
  const statusHasDetails = Boolean(status?.details);
  return (
    <div className="workspace-stage-header">
      <div className="workspace-stage-heading">
        <p className="workspace-stage-kicker">{kicker}</p>
        <h2 className="workspace-stage-title">{title}</h2>
        <p className="workspace-stage-copy">{description}</p>
      </div>

      {(actions || status) && (
        <div className="workspace-stage-actions">
          {actions}
          {status && (
            <div className="workspace-stage-status">
              {statusHasDetails ? (
                <button
                  type="button"
                  className={joinClasses(
                    'workspace-stage-pill',
                    status.iconOnly && 'workspace-stage-pill-icon-only',
                    status.tone && stagePillToneClasses[status.tone]
                  )}
                  aria-label={status.ariaLabel || status.label}
                  aria-expanded={statusDetailsOpen}
                  onClick={() => setStatusDetailsOpen((isOpen) => !isOpen)}
                  onKeyDown={(event) => {
                    if (event.key === 'Escape') setStatusDetailsOpen(false);
                  }}
                >
                  {StatusIcon && <StatusIcon className="h-3.5 w-3.5" aria-hidden="true" />}
                  {!status.iconOnly && <span>{status.label}</span>}
                </button>
              ) : (
                <div
                  role="status"
                  aria-live="polite"
                  className={joinClasses(
                    'workspace-stage-pill',
                    status.tone && stagePillToneClasses[status.tone]
                  )}
                >
                  {StatusIcon && (
                    <StatusIcon
                      className={joinClasses('h-3.5 w-3.5', status.loading && 'animate-spin')}
                      aria-hidden="true"
                    />
                  )}
                  <span>{status.label}</span>
                </div>
              )}
              {statusHasDetails && (
                <div
                  role="status"
                  aria-live="polite"
                  className={joinClasses(
                    'workspace-stage-status-popover',
                    statusDetailsOpen && 'workspace-stage-status-popover-open'
                  )}
                >
                  <p>{status.details.title}</p>
                  <span>{status.details.description}</span>
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export const WorkspaceChecklist = ({
  title,
  items = [],
  className = '',
}) => (
  <section
    aria-label={title}
    className={`soft-card-muted workspace-stage-checklist workspace-checklist ${className}`}
  >
    <div className="workspace-checklist-heading">
      <h3>{title}</h3>
      <span>{items.length} essentials</span>
    </div>
    <ol className="workspace-checklist-grid">
      {items.map((item, index) => (
        <li className="workspace-checklist-item" key={item.title}>
          <span className="workspace-checklist-index" aria-hidden="true">
            {String(index + 1).padStart(2, '0')}
          </span>
          <div>
            <h4>{item.title}</h4>
            <span>{item.description || item.copy}</span>
          </div>
        </li>
      ))}
    </ol>
  </section>
);

export const WorkspaceStageCard = ({
  kicker,
  title,
  description,
  actions = null,
  status = null,
  checklist = null,
  contentClassName = '',
  bodyClassName = '',
  children,
}) => {
  const showInlineError = status?.tone === 'danger' && Boolean(status?.details);
  const InlineErrorIcon = status?.icon;

  return (
    <div className="workspace-stage-card workspace-stage-card-standard-height">
      <WorkspaceStageHeader
        kicker={kicker}
        title={title}
        description={description}
        actions={actions}
        status={showInlineError ? null : status}
      />

      {showInlineError && (
        <section
          className="workspace-stage-error-alert"
          role="alert"
          aria-live="assertive"
          aria-atomic="true"
        >
          <span className="workspace-stage-error-icon" aria-hidden="true">
            {InlineErrorIcon && <InlineErrorIcon className="h-5 w-5" />}
          </span>
          <div className="min-w-0">
            <h3>{status.details.title}</h3>
            <p>{status.details.description}</p>
          </div>
        </section>
      )}

      <div
        className={joinClasses(
          'workspace-stage-body',
          checklist && 'workspace-stage-body-checklisted',
          bodyClassName
        )}
      >
        <div className={joinClasses('workspace-stage-content', contentClassName)}>
          {children}
        </div>
        {checklist}
      </div>
    </div>
  );
};
